import { QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  DropOffSlot,
  IAllocateDropOffRequest,
  IDropOffLeadRow,
  IDropOffLeadRowResponse,
  IDropOffLeadRowsResponse,
} from "../models/DropOffPlanner";
import { DefaultResponse } from "../models/system";

interface UseDropOffPlannerArgs {
  from?: string;
  to?: string;
}

interface DropOffMutationContext {
  previousUnscheduled: IDropOffLeadRowsResponse | undefined;
  previousScheduled: [QueryKey, IDropOffLeadRowsResponse | undefined][];
}

const findLeadInQueries = (
  leadId: string,
  queries: [QueryKey, IDropOffLeadRowsResponse | undefined][]
): IDropOffLeadRow | undefined => {
  for (const [, value] of queries) {
    const match = value?.data?.find((l) => l.id === leadId);
    if (match) return match;
  }
  return undefined;
};

export default function useDropOffPlanner({ from, to }: UseDropOffPlannerArgs = {}) {
  const { get, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const unscheduled = useQuery({
    queryKey: ["dropoff-unscheduled"],
    queryFn: () => get<IDropOffLeadRowsResponse>("/dropoffplanner/unscheduled"),
    staleTime: 1000 * 30,
  });

  const scheduled = useQuery({
    queryKey: ["dropoff-scheduled", from, to],
    queryFn: () => get<IDropOffLeadRowsResponse>(`/dropoffplanner?from=${from}&to=${to}`),
    enabled: !!from && !!to,
    staleTime: 1000 * 30,
  });

  const invalidate = () => {
    queryClient.invalidateQueries(["dropoff-unscheduled"]);
    queryClient.invalidateQueries(["dropoff-scheduled"]);
  };

  const snapshot = async (): Promise<DropOffMutationContext> => {
    await queryClient.cancelQueries(["dropoff-unscheduled"]);
    await queryClient.cancelQueries(["dropoff-scheduled"]);

    return {
      previousUnscheduled: queryClient.getQueryData<IDropOffLeadRowsResponse>([
        "dropoff-unscheduled",
      ]),
      previousScheduled: queryClient.getQueriesData<IDropOffLeadRowsResponse>([
        "dropoff-scheduled",
      ]),
    };
  };

  const rollback = (context: DropOffMutationContext | undefined) => {
    if (!context) return;
    if (context.previousUnscheduled) {
      queryClient.setQueryData(["dropoff-unscheduled"], context.previousUnscheduled);
    }
    context.previousScheduled.forEach(([key, value]) => {
      queryClient.setQueryData(key, value);
    });
  };

  const allocateLead = useMutation<
    IDropOffLeadRowResponse,
    unknown,
    { leadId: string, body: IAllocateDropOffRequest },
    DropOffMutationContext
  >(
    ({ leadId, body }) =>
      put<IAllocateDropOffRequest, IDropOffLeadRowResponse>(
        `/dropoffplanner/${leadId}/allocate`,
        body
      ),
    {
      onMutate: async ({ leadId, body }) => {
        const context = await snapshot();

        const source =
          context.previousUnscheduled?.data?.find((l) => l.id === leadId) ??
          findLeadInQueries(leadId, context.previousScheduled);

        if (!source) return context;

        const updated: IDropOffLeadRow = {
          ...source,
          scheduledCollectionOn: body.scheduledOn,
          scheduledCollectionSlot: body.slot,
        };

        if (context.previousUnscheduled) {
          queryClient.setQueryData<IDropOffLeadRowsResponse>(["dropoff-unscheduled"], {
            ...context.previousUnscheduled,
            data: context.previousUnscheduled.data.filter((l) => l.id !== leadId),
          });
        }

        queryClient.setQueriesData<IDropOffLeadRowsResponse>(
          ["dropoff-scheduled"],
          (current) => {
            if (!current) return current;
            const others = current.data.filter((l) => l.id !== leadId);
            return { ...current, data: [...others, updated] };
          }
        );

        return context;
      },
      onError: (_err, _vars, context) => rollback(context),
      onSettled: () => invalidate(),
    }
  );

  const clearAllocation = useMutation<
    DefaultResponse,
    unknown,
    string,
    DropOffMutationContext
  >((leadId) => del(`/dropoffplanner/${leadId}/allocation`), {
    onMutate: async (leadId) => {
      const context = await snapshot();

      const source = findLeadInQueries(leadId, context.previousScheduled);

      queryClient.setQueriesData<IDropOffLeadRowsResponse>(
        ["dropoff-scheduled"],
        (current) => {
          if (!current) return current;
          return { ...current, data: current.data.filter((l) => l.id !== leadId) };
        }
      );

      if (source && context.previousUnscheduled) {
        const cleared: IDropOffLeadRow = {
          ...source,
          scheduledCollectionOn: null,
          scheduledCollectionSlot: DropOffSlot.AllDay,
        };
        const others = context.previousUnscheduled.data.filter((l) => l.id !== leadId);
        queryClient.setQueryData<IDropOffLeadRowsResponse>(["dropoff-unscheduled"], {
          ...context.previousUnscheduled,
          data: [cleared, ...others],
        });
      }

      return context;
    },
    onError: (_err, _leadId, context) => rollback(context),
    onSettled: () => invalidate(),
  });

  const clearDay = useMutation(
    ({ date, slot }: { date: string, slot?: DropOffSlot }) => {
      const qs = slot !== undefined ? `&slot=${slot}` : "";
      return del<DefaultResponse>(`/dropoffplanner/day?date=${date}${qs}`);
    },
    {
      onSuccess: () => invalidate(),
    }
  );

  return {
    unscheduled,
    scheduled,
    allocateLead,
    clearAllocation,
    clearDay,
  };
}
